import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, MessageCircle, Smartphone, CheckCircle2, X, Loader2, ShoppingBag } from "lucide-react";
import { whatsappOrderUrl, MOMO_MERCHANT_ID, MOMO_SHORT_CODE, MOMO_INSTRUCTIONS } from "@/lib/ordering";
import { useCart } from "@/lib/CartContext";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImg = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);

const LINE_LABEL = {
  classic: "Classic",
  labour: "Eve Labour",
  fluffy: "Fluffy",
};

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState([]);
  const [showMomo, setShowMomo] = useState(false);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setProduct(null); setNotFound(false);
    axios.get(`${API}/products/${id}`)
      .then((r) => { if (!cancelled) setProduct(r.data); })
      .catch(() => { if (!cancelled) setNotFound(true); });
    axios.get(`${API}/products`)
      .then((r) => { if (!cancelled) setRelated(r.data.products || []); })
      .catch(() => {});
    axios.get(`${API}/payments/config`)
      .then((r) => { if (!cancelled) setPaymentsEnabled(!!r.data.enabled); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [id]);

  if (notFound) {
    return (
      <div data-testid="product-not-found" className="max-w-3xl mx-auto px-4 py-32 text-center">
        <h1 className="font-heading text-4xl">Product not found.</h1>
        <p className="mt-3 text-faytex-muted">This product may have been removed from our catalog.</p>
        <Link to="/shop" data-testid="pd-back-to-shop" className="inline-flex items-center gap-2 mt-8 rounded-full bg-faytex-pink hover:bg-faytex-pinkHover text-white px-6 py-3 text-sm font-semibold">
          <ArrowLeft className="w-4 h-4" /> Back to shop
        </Link>
      </div>
    );
  }
  if (!product) {
    return <div className="max-w-7xl mx-auto px-4 py-40 text-center text-faytex-muted">Loading…</div>;
  }

  const gallery = [product.image, ...(product.gallery || [])].filter(Boolean);
  const others = related.filter((p) => p.id !== product.id && p.line === product.line).slice(0, 3);
  const orderUrl = whatsappOrderUrl(product);

  return (
    <div data-testid="product-detail-page" className="bg-white">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 md:pt-14">
        <Link to="/shop" data-testid="pd-back" className="inline-flex items-center gap-2 text-sm text-faytex-muted hover:text-faytex-pink">
          <ArrowLeft className="w-4 h-4" /> Back to Shop
        </Link>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-16 grid lg:grid-cols-2 gap-12">
        {/* GALLERY */}
        <div>
          <div
            data-testid="pd-hero-image"
            className="aspect-square rounded-3xl overflow-hidden bg-faytex-pinkLight/40 flex items-center justify-center p-10"
            style={{ borderTop: `4px solid ${product.color || "#F071AD"}` }}
          >
            <img src={resolveImg(product.image)} alt={product.name} className="max-h-full object-contain" />
          </div>
          {gallery.length > 1 && (
            <div className="mt-4 grid grid-cols-4 gap-3">
              {gallery.slice(0, 4).map((g, i) => (
                <div key={i} className="aspect-square rounded-2xl overflow-hidden bg-faytex-pinkLight/40 p-3">
                  <img src={resolveImg(g)} alt="" className="w-full h-full object-contain" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* DETAILS */}
        <div>
          <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">
            {LINE_LABEL[product.line] || product.line}
            {product.absorbency ? ` • ${product.absorbency}` : ""}
          </p>
          <h1 data-testid="pd-name" className="font-heading text-4xl sm:text-5xl md:text-6xl mt-3 tracking-tighter">{product.name}</h1>
          {product.tagline && <p className="mt-4 text-lg text-faytex-muted">{product.tagline}</p>}

          <div className="mt-8 flex items-end gap-5">
            <span data-testid="pd-price" className="font-heading text-4xl text-faytex-pink">GH₵ {product.price_ghs}</span>
            <span className="text-sm text-faytex-muted mb-1">{product.count || 10}-pack</span>
          </div>

          {product.description && (
            <p className="mt-6 text-faytex-ink leading-relaxed whitespace-pre-line">{product.description}</p>
          )}

          {product.features?.length > 0 && (
            <ul className="mt-6 space-y-2 text-sm">
              {product.features.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-faytex-pink" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}

          {/* CTAs */}
          <div className="mt-10 grid sm:grid-cols-2 gap-3">
            <button
              onClick={() => { addItem(product, 1); toast.success(`${product.name} added to cart`); }}
              data-testid="pd-add-to-cart"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-faytex-pink hover:bg-faytex-pinkHover text-white px-6 py-3.5 text-sm font-semibold transition-colors"
            >
              <ShoppingBag className="w-4 h-4" /> Add to cart
            </button>
            <button
              onClick={() => setShowMomo(true)}
              data-testid="pd-momo-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-faytex-ink hover:bg-faytex-pink text-white px-6 py-3.5 text-sm font-semibold transition-colors"
            >
              <Smartphone className="w-4 h-4" /> Buy now
            </button>
          </div>
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            <a
              href={orderUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pd-whatsapp-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:brightness-95 text-white px-6 py-3 text-sm font-semibold transition"
            >
              <MessageCircle className="w-4 h-4" /> Order on WhatsApp
            </a>
            <button
              onClick={() => navigate("/cart")}
              data-testid="pd-view-cart"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white border border-gray-200 hover:border-faytex-pink text-faytex-ink px-6 py-3 text-sm font-semibold transition-colors"
            >
              View cart <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div className="mt-8 rounded-2xl bg-faytex-pinkLight/60 p-5 text-sm">
            <p className="font-heading text-lg">Also available at your Market Queen.</p>
            <p className="text-faytex-muted mt-1">
              Find Faytex at 1,400+ stalls in Makola, Madina and Kejetia — see our{" "}
              <Link to="/market-queens" className="text-faytex-pink underline underline-offset-2 hover:text-faytex-pinkHover">Market Queens page</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* RELATED */}
      {others.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-gray-100">
          <div className="flex items-end justify-between mb-8">
            <h2 className="font-heading text-3xl md:text-4xl tracking-tight">You may also like</h2>
            <Link to="/shop" className="text-sm font-semibold text-faytex-pink hover:underline">All products →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {others.map((p) => (
              <Link
                key={p.id}
                to={`/shop/${p.id}`}
                data-testid={`pd-related-${p.id}`}
                className="group rounded-2xl border border-gray-100 bg-white shadow-soft hover:shadow-hover transition-all overflow-hidden"
              >
                <div className="h-1.5 w-full" style={{ background: p.color || "#F071AD" }} />
                <div className="aspect-square bg-faytex-pinkLight/40 flex items-center justify-center p-8">
                  <img src={resolveImg(p.image)} alt={p.name} className="max-h-full object-contain group-hover:scale-105 transition-transform" />
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-xl">{p.name}</h3>
                  <p className="text-sm text-faytex-muted mt-1">GH₵ {p.price_ghs}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* MoMo Modal */}
      {showMomo && (
        <PaymentModal
          product={product}
          orderUrl={orderUrl}
          paymentsEnabled={paymentsEnabled}
          onClose={() => setShowMomo(false)}
        />
      )}
    </div>
  );
}

function PaymentModal({ product, orderUrl, paymentsEnabled, onClose }) {
  const [step, setStep] = useState(paymentsEnabled ? "form" : "instructions");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [channel, setChannel] = useState("mobile_money"); // mobile_money | card
  const [submitting, setSubmitting] = useState(false);

  async function initiate() {
    if (!email) { toast.error("Please enter your email"); return; }
    setSubmitting(true);
    try {
      const callback_url = `${window.location.origin}/payments/callback`;
      const { data } = await axios.post(`${API}/payments/initialize`, {
        product_id: product.id,
        email,
        quantity,
        channel,
        callback_url,
        customer_name: name || undefined,
        customer_phone: phone || undefined,
      });
      // Full redirect to Paystack hosted checkout (works for both MoMo & card)
      window.location.href = data.authorization_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start payment");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      data-testid="momo-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div className="bg-white rounded-3xl shadow-hover max-w-md w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          data-testid="momo-close"
          aria-label="Close"
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="w-12 h-12 rounded-full bg-faytex-pinkLight text-faytex-pink flex items-center justify-center">
          <Smartphone className="w-5 h-5" />
        </div>
        <h3 className="font-heading text-3xl mt-4">
          {paymentsEnabled ? "Pay for your order" : "Pay with Mobile Money"}
        </h3>
        <p className="text-sm text-faytex-muted mt-2">
          {paymentsEnabled
            ? "Secure checkout powered by Paystack — Mobile Money & card."
            : "Fast, secure, mobile-first checkout for Ghana."}
        </p>

        {paymentsEnabled ? (
          <div className="mt-6 space-y-4" data-testid="momo-checkout-form">
            <div className="rounded-2xl bg-faytex-pinkLight/60 p-4 flex items-center justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-widest text-faytex-muted">Order</p>
                <p className="font-heading text-xl">{product.name}</p>
              </div>
              <p className="font-heading text-2xl text-faytex-pink">GH₵ {product.price_ghs * quantity}</p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {[
                { k: "mobile_money", label: "Mobile Money" },
                { k: "card", label: "Card" },
              ].map((c) => (
                <button
                  key={c.k}
                  type="button"
                  onClick={() => setChannel(c.k)}
                  data-testid={`checkout-channel-${c.k}`}
                  className={`rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors ${
                    channel === c.k
                      ? "bg-faytex-pink text-white border-faytex-pink"
                      : "bg-white border-gray-200 hover:border-faytex-pink text-faytex-ink"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <Input label="Full name" testid="checkout-name" value={name} onChange={setName} />
            <Input label="Email *" type="email" testid="checkout-email" value={email} onChange={setEmail} />
            {channel === "mobile_money" && (
              <Input label="Mobile Money phone (MTN / Vodafone / AirtelTigo)" testid="checkout-phone" value={phone} onChange={setPhone} />
            )}
            <div>
              <label className="text-xs uppercase tracking-widest text-faytex-muted font-semibold">Quantity</label>
              <input
                data-testid="checkout-quantity"
                type="number"
                min={1}
                max={100}
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
                className="mt-2 w-full bg-gray-50 border border-gray-200 focus:border-faytex-pink focus:ring-faytex-pink rounded-xl px-4 py-3 text-sm outline-none"
              />
            </div>

            <button
              onClick={initiate}
              disabled={submitting}
              data-testid="checkout-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-faytex-pink hover:bg-faytex-pinkHover text-white px-6 py-3 text-sm font-semibold transition-colors disabled:opacity-60"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {submitting ? "Redirecting…" : `Pay GH₵ ${product.price_ghs * quantity}`}
            </button>
            <p className="text-[11px] text-faytex-muted text-center">Powered by Paystack. You will be redirected to a secure checkout.</p>
          </div>
        ) : (
          <>
            <div className="mt-5 rounded-2xl bg-faytex-pinkLight/60 p-4">
              <p className="text-[11px] uppercase tracking-widest text-faytex-muted">Merchant ID</p>
              <p className="font-heading text-2xl text-faytex-pink" data-testid="momo-merchant">{MOMO_MERCHANT_ID}</p>
              <p className="text-[11px] uppercase tracking-widest text-faytex-muted mt-3">Dial short code</p>
              <p className="font-heading text-2xl text-faytex-pink">{MOMO_SHORT_CODE}</p>
            </div>
            <ol className="mt-5 space-y-2 text-sm text-faytex-ink">
              {MOMO_INSTRUCTIONS.map((s, i) => (
                <li key={i} className="flex gap-3">
                  <span className="w-6 h-6 flex-shrink-0 rounded-full bg-faytex-pink text-white text-xs font-semibold flex items-center justify-center">{i + 1}</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
            <a
              href={orderUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="momo-followup-whatsapp"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:brightness-95 text-white px-6 py-3 text-sm font-semibold"
            >
              Send confirmation to WhatsApp <ArrowRight className="w-4 h-4" />
            </a>
          </>
        )}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-faytex-muted font-semibold">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-gray-50 border border-gray-200 focus:border-faytex-pink focus:ring-faytex-pink rounded-xl px-4 py-3 text-sm outline-none"
      />
    </div>
  );
}
